import React from 'react';
import { Icons } from '../../../components/ui/Icons';
import { useStagger } from '../hooks/useScrollReveal';

const features = [
  {
    icon: Icons.Gmail,
    title: 'Email on autopilot',
    desc: 'Draft, triage, and summarize entire threads without opening your inbox.',
    tag: 'Gmail',
    accent: 'from-red-100/60 to-orange-50/40',
  },
  {
    icon: Icons.GoogleCalendar,
    title: 'Zero-friction scheduling',
    desc: 'Find open slots across teams, create events, and send invites — in one sentence.',
    tag: 'Calendar',
    accent: 'from-blue-100/60 to-indigo-50/40',
  },
  {
    icon: () => <img src="https://upload.wikimedia.org/wikipedia/commons/4/45/Notion_app_logo.png" alt="Notion" className="w-7 h-7 object-contain" />,
    title: 'Docs that write themselves',
    desc: 'Turn meeting notes into structured Notion pages — formatted, tagged, and ready.',
    tag: 'Notion',
    accent: 'from-gray-100/60 to-slate-50/40',
  },
  {
    icon: Icons.GoogleSheets,
    title: 'Data entry, automated',
    desc: 'Append rows, update trackers, sync spreadsheets — all by describing what you need.',
    tag: 'Sheets',
    accent: 'from-green-100/60 to-emerald-50/40',
  },
  {
    icon: () => <img src="https://cdn-icons-png.flaticon.com/128/3800/3800024.png" alt="Slack" className="w-7 h-7 object-contain" />,
    title: 'Team comms on cruise control',
    desc: 'Post standups, share digests, and keep channels informed — automatically.',
    tag: 'Slack',
    accent: 'from-purple-100/60 to-fuchsia-50/40',
  },
  {
    icon: Icons.GoogleDrive,
    title: 'Files at your fingertips',
    desc: 'Search, share, and organize across your entire Drive — without digging.',
    tag: 'Drive',
    accent: 'from-amber-100/60 to-yellow-50/40',
  },
];

const LandingFeatures: React.FC = () => {
  const gridRef = useStagger(100);

  return (
    <section id="features" className="relative py-10 md:py-14 bg-white overflow-hidden scroll-mt-20">
      <div className="section-line" />

      <div className="max-w-7xl mx-auto px-6 pt-2">
        {/* Header — asymmetric left-aligned */}
        <div className="max-w-2xl mb-16 md:mb-20">
          <p className="text-[11px] font-semibold text-black/30 tracking-[0.2em] uppercase mb-4">Integrations</p>
          <h2 className="text-[32px] md:text-[46px] font-semibold tracking-[-0.04em] text-black leading-[1.1] mb-4">
            Every tool you use,<br />working together
          </h2>
          <p className="text-[17px] text-black/40 font-normal leading-relaxed">
            Six deep integrations. Dozens of automations. One command.
          </p>
        </div>

        {/* Bento Grid — asymmetric */}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((f, i) => (
            <div
              key={i}
              className={`sr-scale delay-${i + 1} group relative bg-white rounded-2xl p-7 border border-black/[0.05] hover-lift cursor-default overflow-hidden`}
            >
              {/* Hover gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${f.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl`} />

              <div className="relative z-10">
                <div className="w-12 h-12 rounded-[0.875rem] bg-white border border-black/[0.05] flex items-center justify-center mb-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] group-hover:scale-105 transition-transform duration-300">
                  <f.icon className="w-6 h-6 object-contain" />
                </div>
                <h3 className="text-[18px] md:text-[22px] font-medium text-black tracking-[-0.02em] mb-2">{f.title}</h3>
                <p className="text-[14px] text-black/40 leading-relaxed font-normal">{f.desc}</p>
                <div className="mt-5 pt-4 border-t border-black/[0.04]">
                  <span className="text-[10px] font-semibold text-black/20 tracking-[0.12em] uppercase">{f.tag}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LandingFeatures;
